import type { SourceHighlightData } from '@/stores/assistant-store'
import type { HighlightData, OpenPreviewParams, TextHighlight } from '@/stores/preview-store'
import type { CitationRef } from '@/lib/api'
import { trackCitationClick } from '@/lib/citation-tracking'

/**
 * The page to show on a citation label, or null when there is no real page.
 *
 * Word and Excel documents have no pages, and their chunks are stored with
 * page 0 — so 0 means "no page", not "page zero". Every citation label that
 * prints a page should go through this, or a .docx citation reads "Page 0".
 */
export function displayCitationPage(page: number | null | undefined): number | null {
  return typeof page === 'number' && Number.isFinite(page) && page > 0 ? page : null
}

/** `"<document> p.<page>"`, or just the document name when there is no real page. */
export function formatSourceWithPage(document: string, page: number | null | undefined): string {
  const shown = displayCitationPage(page)
  return shown != null ? `${document} p.${shown}` : document
}

/**
 * Resolve the target page to open when a user clicks a citation.
 *
 * When a chunk spans pages (e.g., bottom of p.4 → top of p.5), the highlight's
 * `pages` array is sorted ascending, so `pages[0]` is the earliest page even
 * when the cited sentence sits on a later page. Prefer the citation's stated
 * page when the highlight actually covers it; otherwise fall back to the first
 * highlighted page; finally fall back to the stated page.
 */
export function resolveCitationTargetPage(
  previewHL: { highlight: HighlightData } | { textHighlight: TextHighlight } | undefined,
  citationPage: number | null | undefined,
): number | undefined {
  const highlightPages = previewHL && 'highlight' in previewHL ? previewHL.highlight.pages : null

  if (citationPage != null && highlightPages?.some((p) => p.pageNumber === citationPage)) {
    return citationPage
  }

  return highlightPages?.[0]?.pageNumber ?? citationPage ?? undefined
}

/**
 * Convert source highlight data to preview store format.
 * V1 (PDF bounding boxes) → { highlight: { id, pages } }
 * V2 (text-search)        → { textHighlight: { chunkText, sectionHeader } }
 *
 * The `citationId` is used as `highlight.id` for V1 so the PDF overlay can diff
 * highlights between renders. It must be stable per citation.
 */
export function toPreviewHighlight(
  sourceData: SourceHighlightData | null | undefined,
  citationId: string,
): { highlight: HighlightData } | { textHighlight: TextHighlight } | undefined {
  if (!sourceData) return undefined

  if (sourceData.v === 2) {
    return {
      textHighlight: {
        chunkText: sourceData.chunkText,
        sectionHeader: sourceData.sectionHeader,
      },
    }
  }

  if (sourceData.v === 1 && sourceData.pages.length > 0) {
    return {
      highlight: {
        id: citationId,
        pages: sourceData.pages.map((p) => ({
          pageNumber: p.pageNumber,
          rects: p.rects.map((r) => ({ x1: r.x1, y1: r.y1, x2: r.x2, y2: r.y2 })),
        })),
      },
    }
  }

  return undefined
}

export interface PrecisePreviewHighlight {
  highlight?: HighlightData
  textHighlight?: TextHighlight
}

/**
 * Quote-first preview payload (citation-highlight-precision spec Part 2).
 *
 * - V1 (PDF rects) + verbatim quote → keep the rects for page targeting and
 *   as a fallback when the text-layer search misses, but drive the visible
 *   highlight from the quote.
 * - V1 without a quote → rects only (previous behavior).
 * - V2 (DOCX/Excel text-search) → unchanged; those viewers are out of scope.
 * - No highlight data but a quote → quote text-search (legacy rows).
 */
export function toPrecisePreviewHighlight(
  sourceData: SourceHighlightData | null | undefined,
  sourceQuote: string | null | undefined,
  citationId: string,
): PrecisePreviewHighlight | undefined {
  const base = toPreviewHighlight(sourceData, citationId)
  const quote = sourceQuote?.trim() || null

  if (base && 'highlight' in base) {
    return quote
      ? { highlight: base.highlight, textHighlight: { chunkText: quote, sectionHeader: null } }
      : { highlight: base.highlight }
  }

  if (base && 'textHighlight' in base) {
    return { textHighlight: base.textHighlight }
  }

  return quote ? { textHighlight: { chunkText: quote, sectionHeader: null } } : undefined
}

/**
 * Open a drafted citation's source in the document preview, at its page and
 * with its highlight. The one path every drafting surface uses: the editor's
 * inline citation tooltip and the footer's sources list.
 *
 * `context: 'library'` is explicit ("modal, no dock"): drafting surfaces render
 * no docked reader, and `openPreview` otherwise INHERITS the previous context
 * for a still-cached document — a stale 'chat'/'analysis' would self-hide the
 * modal and make the click do nothing. (The 'drafting' on trackCitationClick is
 * telemetry, unrelated to the preview store.)
 */
export function openCitationSource(
  ref: CitationRef,
  isLibrary: boolean,
  openPreview: (params: OpenPreviewParams) => Promise<void>,
): void {
  trackCitationClick({
    documentId: ref.documentId,
    documentName: ref.documentName,
    citationIndex: ref.citationIndex,
    pageNumber: ref.pageNumber,
    context: 'drafting',
  })

  const precise = toPrecisePreviewHighlight(
    ref.highlightData ?? undefined,
    ref.quote ?? null,
    `inline-${isLibrary ? 'lib-' : ''}${ref.documentId}-${ref.citationIndex}`,
  )
  const targetPage = resolveCitationTargetPage(
    precise?.highlight ? { highlight: precise.highlight } : undefined,
    ref.pageNumber,
  )

  void openPreview({
    documentId: ref.documentId,
    documentName: ref.documentName,
    context: 'library',
    ...(targetPage != null ? { page: targetPage } : {}),
    ...(precise?.highlight ? { highlight: precise.highlight } : {}),
    ...(precise?.textHighlight ? { textHighlight: precise.textHighlight } : {}),
  })
}
